import "./map-component.css";
import "leaflet/dist/leaflet.css";
import {
    Circle,
    FeatureGroup,
    ImageOverlay,
    MapContainer,
    Marker,
    Polygon,
    Polyline,
    Rectangle,
    Tooltip,
    useMapEvents
} from "react-leaflet";
import L from "leaflet";
import React, {useCallback, useEffect, useRef, useState} from "react";
import Sidebar from "./sidebar/sidebar";
import {MarkerFaction, MarkerTypes} from "../../shared/data/marker-type.enum";
import {toast} from "react-toastify";
import ConfirmDialog from "../../shared/ui/dialog/confirm-dialog/confirm-dialog";
import {useNavigate, useParams} from "react-router-dom";
import {Client} from "@stomp/stompjs";
import SockJS from "sockjs-client";
import InviteDialog from "../../shared/ui/dialog/invite-dialog/invite-dialog";
import {EditControl} from "react-leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";

import GridOverlay from "./GridOverlay";
import {markerProperties} from "../../shared/data/MarkerProperties";
import capPoints from "../../shared/data/cap-points";
import SelectLayoutDialog from "../../shared/ui/dialog/select-layout-dialog/select-layout-dialog";
import {Factions} from "../../shared/data/faction.enum";
import {mapProperties} from "../../shared/data/map-properties";
import {MapLayouts} from "../../shared/data/map-layout.enum";
import SelectFactionDialog from "../../shared/ui/dialog/select-faction-dialog/select-faction-dialog"; // Import your cap points
import boundChecker from './bound-checker';
import SettingsSidebar from "./settings-sidebar/settings-sidebar";
import LoadingDialog from "../../shared/ui/dialog/loading-dialog/loading-dialog";

// lat = vertical
// lng = horizontal

export default function MapComponent() {
    const [sessionObj, setSessionObj] = useState({
        sessionId: null,
        sessionOwner: {
            id: null,
            name: null
        },
        guests: [],
        canGuestsEdit: true,
        gameObj: {
            selectedFaction: null,
            selectedCaps: [],
            score: "2:2",
            markers: [],
            shapes: {
                polylines: [],
                polygons: [],
                rectangles: []
            },

        }
    })

    let [loading, setLoading] = useState(false);
    let [loadingTitle, setLoadingTitle] = useState("Loading")

    let initialLoad = true;

    const localUpdateRef = useRef(false);
    const [myUser, setMyUser] = useState(null);
    const [stompClient, setStompClient] = useState(null);
    const [sessionUrl, setSessionUrl] = useState("");
    // A ref to track if we've already connected.
    const hasConnectedRef = useRef(false);

    const [selectedMarker, setSelectedMarker] = useState(null);
    const navigate = useNavigate();

    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [modalAction, setModalAction] = useState(null);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

    const [showSelectLayoutDialog, setShowSelectLayoutDialog] = useState(false)
    const [showSelectFactionDialog, setShowSelectFactionDialog] = useState(true)

    const [showRadius, setShowRadius] = useState(true); // Toggle for radius visibility
    const [showGrid, setShowGrid] = useState(true);
    const [showCaps, setShowCaps] = useState(true);
    const [showScore, setShowScore] = useState(true);

    const { gameMap } = useParams();
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";

    const isOwner = (user = myUser, owner = sessionObj?.sessionOwner) => {
        if (sessionObj.sessionOwner?.id === null) {
            return false;
        } else if (myUser === null) {
            return false;
        }

        return user.id === owner.id;
    }

    // game state
    const mapData = mapProperties[gameMap] || null;
    const [factions, setFactions] = useState(null);
    const [axisScore, setAxisScore] = useState(2);
    const [alliesScore, setAlliesScore] = useState(2);
    const [layout, setLayout] = useState(null);

    const garrison_max = 8;

    const getGarrisonCount = (markerFaction) => {
        return sessionObj.gameObj.markers.filter((marker) => marker.type === MarkerTypes.GARRISON && marker.faction === markerFaction).length
    }

    const getEnemyFaction = () => {
        console.log("Get enemy faction: ", sessionObj.gameObj.selectedFaction === Factions.AXIS ? Factions.ALLIES : Factions.AXIS)
        return sessionObj.gameObj.selectedFaction === Factions.AXIS ? Factions.ALLIES : Factions.AXIS;
    }

    useEffect(() => {
        if (mapData) {
            console.log("Map Data = ", mapData)
            setLayout(mapData.layout);
            const leftFaction = mapData.factions[0]; // Faction on the "left" (horizontal) or "bottom" (vertical)
            const rightFaction = mapData.factions[1]; // Faction on the "right" (horizontal) or "top" (vertical)
            setFactions({ left: leftFaction, right: rightFaction });
            console.log("factions for this map: ", { left: leftFaction, right: rightFaction });
        } else {
            console.error(`Map "${gameMap}" not found!`);
            navigate("/maps");
        }
    }, [gameMap, mapData, navigate]);

    const imageWidth = 2000;
    const imageHeight = 2000;
    const imageBounds = [
        [0, 0], // Top-left corner
        [imageHeight, imageWidth], // Bottom-right corner
    ];

    // Constants
    const SECTOR_SIZE = 400;  // Each sector is 400m

    const ControlledDistance = (faction) => {
        switch(faction) {
            case Factions.AXIS:
                return axisScore * SECTOR_SIZE;
            case Factions.ALLIES:
                return alliesScore * SECTOR_SIZE;
            default:
                return 800;
        }
    }

    useEffect(() => {
        let scores = sessionObj.gameObj.score.split(":").map(Number);
        if (sessionObj.gameObj.selectedFaction === Factions.AXIS) {
            setAxisScore(scores[0]);
            setAlliesScore(scores[1]);
        } else {
            setAlliesScore(scores[0]);
            setAxisScore(scores[1]);
        }
    }, [sessionObj.gameObj.score, sessionObj.gameObj.selectedFaction]);

    function getSelectedCapPoints() {
        // Ensure valid map selection
        if (!gameMap || !capPoints[gameMap]) {
            console.error(`Map "${gameMap}" not found in capPoints.`);
            return [];
        }

        // Get the cap points for the selected map
        const mapCapPoints = capPoints[gameMap];

        // Ensure selectedCaps is valid
        if (!sessionObj.gameObj.selectedCaps || sessionObj.gameObj.selectedCaps.length === 0) {
            return []; // Return empty array to prevent errors
        }

        return sessionObj.gameObj.selectedCaps.map((capIndex, index) => {
            const section = mapCapPoints[index];

            // Ensure section exists and capIndex is within range
            if (!section || capIndex === null || capIndex === undefined || capIndex >= section.caps.length) {
                return null; // Skip invalid selections
            }
            return section.caps[capIndex];
        }).filter(cap => cap !== null); // Remove null entries
    }


    // Memoized WebSocket connection function.
    const connectToWebSocket = useCallback((sessionId, myUser, sessionOwner) => {
        const startTime = performance.now(); // Start measuring time
        setLoadingTitle("Connecting to Session");

        if (myUser != null) {
            console.log("Connecting to WebSocket with user:", myUser);
            const socket = new SockJS(`${BACKEND_URL}/ws`);
            const client = new Client({
                webSocketFactory: () => socket,
                connectHeaders: {},
                onConnect: () => {
                    console.log("Connected to WebSocket for session:", sessionId);
                    resolveWebSocket(); // Resolve the connection promise

                    client.subscribe(`/topic/sessions/${sessionId}/sessionObj`, (message) => {
                        const updatedSessionObj = JSON.parse(message.body);

                        setSessionObj((prevSessionObj) => {
                            console.log("Previous sessionObj", prevSessionObj);
                            console.log("Update received", updatedSessionObj);
                            if (!initialLoad) {
                                handleUserChange(prevSessionObj.guests, updatedSessionObj.guests)
                            }
                            return updatedSessionObj;
                        });

                        initialLoad = false;
                    });

                    if (myUser?.id !== sessionOwner?.id) {
                        console.log("Notifying others you've joined session:", sessionId);
                        client.publish({
                            destination: `/app/sessions/${sessionId}/users/add`,
                            body: JSON.stringify(myUser),
                        });
                    }
                },
                onDisconnect: () => {
                    client.publish({
                        destination: `/app/sessions/${sessionId}/users/remove`,
                        body: JSON.stringify(myUser),
                    });
                    setTimeout(() => {
                        client.deactivate();
                    }, 500);
                },
                onStompError: (error) => console.error("STOMP Error:", error),
            });

            client.activate();
            setStompClient(client);

            // Create a Promise that resolves when the WebSocket is connected
            let resolveWebSocket;
            const websocketPromise = new Promise((resolve) => {
                resolveWebSocket = resolve;
            });

            websocketPromise.then(() => {
                const elapsedTime = performance.now() - startTime; // Calculate connection time

                if (elapsedTime > 100) {
                    setLoading(true); // Show loader if connection took more than 100ms

                    // Ensure at least 1 second of loading
                    setTimeout(() => {
                        finalizeWebSocketConnection(myUser, sessionOwner);
                    }, 2000);
                } else {
                    finalizeWebSocketConnection(myUser, sessionOwner); // No delay if fast
                }
            });
        }
    }, [myUser, BACKEND_URL]);

    function finalizeWebSocketConnection(myUser, sessionOwner) {
        setLoading(false); // Stop loading
        toast.success("Connected to Session");
        if (myUser?.id === sessionOwner?.id) {
            setIsInviteModalOpen(true);
        }
    }

    function handleUserChange(oldGuests, newGuests) {
        if (oldGuests.length === newGuests.length) {

        } else if (oldGuests.length < newGuests.length) {
            toast.success(`${JSON.stringify(newGuests[newGuests.length - 1].name)} joined!`)
        } else {
            let leftUsers = oldGuests.filter((oldUser) => !newGuests.includes(oldUser))
            toast.warn(`${leftUsers[0].name} left!`)
        }
    }

    function createSession() {
        const startTime = performance.now(); // Start measuring time
        let sessionPromise = Promise.resolve(); // Default resolved promise

        setLoadingTitle("Creating Session");

        let sessionId;
        let myUser;
        let sessionOwner;

        const fetchPromise = fetch(`${BACKEND_URL}/api/maps/${gameMap}/sessions`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ gameObj: sessionObj.gameObj }),
        })
            .then(res => res.text()) // Convert response to text
            .then(response => {
                const data = JSON.parse(response);
                console.log("Session create response: ", data);

                myUser = data.sessionObj.sessionOwner;
                sessionOwner = data.sessionObj.sessionOwner;
                sessionId = data.sessionObj.sessionId;

                setMyUser(myUser);
                setSessionObj(prev => ({
                    ...prev,
                    sessionId: sessionId,
                    sessionOwner: sessionOwner,
                }));

                navigate(`?sessionId=${sessionId}`);
                setSessionUrl(window.location.href);
            })
            .catch(error => {
                console.error("Error creating session:", error);
            });

        fetchPromise.then(() => {
            const elapsedTime = performance.now() - startTime; // Calculate elapsed time

            if (elapsedTime > 100) {
                setLoading(true); // Show loader if fetch took more than 100ms

                // Ensure at least 1 second of loading
                setTimeout(() => {
                    finalizeSessionCreation(sessionId, myUser, sessionOwner);
                }, 1000);
            } else {
                finalizeSessionCreation(sessionId, myUser, sessionOwner); // No delay if fast
            }
        });
    }

    function finalizeSessionCreation(sessionId, myUser, sessionOwner) {
        setLoading(false); // Stop loading
        setIsInviteModalOpen(true);
        initialLoad = false;
        connectToWebSocket(sessionId, myUser, sessionOwner);
    }

    function syncSessionObj(sessionObj) {
        console.log("sync sessionObj", sessionObj);
        if (stompClient && stompClient.connected) {
            const payload = JSON.stringify({sessionObj: sessionObj});
            stompClient.publish({
                destination: `/app/sessions/${sessionObj.sessionId}/sessionObj`,
                body: payload,
            });
        }
    }

    const fetchNewUser = useCallback(
        async () => {
            console.log("Fetch new user");
            const res = await fetch(`${BACKEND_URL}/api/users/name`, {
                method: "GET",
            });
            const response = await res.text();
            const data = JSON.parse(response);
            console.log("got new user: ", data);
            return data;
        },
        [BACKEND_URL]
    );

    // runs once on mount
    useEffect(() => {
        console.log("Fetching URL");
        const urlParams = new URLSearchParams(window.location.search);
        const existingSessionId = urlParams.get("sessionId");
        if (existingSessionId) {
            setShowSelectFactionDialog(false)
            setShowSelectLayoutDialog(false);
            setSessionObj((prev) => ({
                ...prev,
                    sessionId: existingSessionId
            }));

            const url = `${BACKEND_URL}/api/maps/${gameMap}/sessions/${existingSessionId}`;

            fetch(url)
                .then((response) => response.json())
                .then((isValid) => {
                    if (!isValid) {
                        // If session is not valid, clear it and show a message
                        setSessionObj((prev) => ({
                            ...prev,
                            sessionId: null
                        }));
                        toast.error("Invalid Session");
                        navigate('/')
                    } else {
                        fetchNewUser().then((user) => {
                            let myUser = user;
                            setMyUser(myUser);
                            console.log("Fetched non-owner user, connecting WebSocket as non-owner");
                            connectToWebSocket(existingSessionId, myUser, null);
                        });
                    }
                })
                .catch((error) => {
                    console.error("Error checking session validity:", error);
                    toast.error("Error checking session validity.");
                });
        }
    }, []);

    function leaveSession() {
        const startTime = performance.now(); // Start measuring time
        let stompPromise = Promise.resolve(); // Default resolved promise

        if (stompClient && stompClient.connected) {
            console.log(`Notifying backend that user ${myUser?.name} is leaving...`);

            stompClient.publish({
                destination: `/app/sessions/${sessionObj.sessionId}/users/remove`,
                body: JSON.stringify(myUser),
            });

            stompPromise = stompClient.deactivate()
                .then(() => console.log("WebSocket client fully deactivated."))
                .catch(error => console.error("Error deactivating WebSocket client:", error));
        }

        stompPromise.then(() => {
            const endTime = performance.now(); // Measure end time
            const elapsedTime = endTime - startTime; // Calculate execution time

            if (elapsedTime > 100) {
                setLoadingTitle("Leaving Session")
                setLoading(true); // Show loader if it took more than 100ms

                // Ensure at least 2 seconds of loading
                setTimeout(() => {
                    finalizeSession();
                }, 1000);
            } else {
                finalizeSession(); // No delay needed if execution was fast
            }
        });
    }

    function finalizeSession() {
        // Reset all session-related state
        setSessionUrl("");
        setSessionObj(prev => ({
            ...prev,
            sessionId: null,
            sessionOwner: null,
            guests: [],
            gameObj: {
                ...prev.gameObj,
                markers: [],
                shapes: {
                    polylines: [],
                    polygons: [],
                    rectangles: []
                },
            }
        }));
        setMyUser(null);
        setStompClient(null);
        hasConnectedRef.current = false;

        // Navigate back to home or default page
        navigate("");

        setLoading(false); // Stop loading
        toast.success("Left Session");
    }

    useEffect(() => {
        const handleTabClose = () => {
            if (stompClient && stompClient.connected && myUser && sessionObj.sessionId) {
                // Send a leave message synchronously. (WebSocket messages are inherently async,
                // so this is not guaranteed to be delivered before the connection closes.)
                stompClient.publish({
                    destination: `/app/sessions/${sessionObj.sessionId}/users/remove`,
                    body: JSON.stringify(myUser),
                });
            }
        };

        window.addEventListener("beforeunload", handleTabClose);
        return () => {
            window.removeEventListener("beforeunload", handleTabClose);
        };
    }, [stompClient, myUser, sessionObj.sessionId]);

    function isGarrisonLimitReached() {
        if (selectedMarker.type === MarkerTypes.GARRISON) {
            if (selectedMarker.faction === MarkerFaction.FRIENDLY && getGarrisonCount(MarkerFaction.FRIENDLY) >= garrison_max) {
                return true;
            } else if (selectedMarker.faction === MarkerFaction.ENEMY && getGarrisonCount(MarkerFaction.ENEMY) >= garrison_max) {
                return true;
            } else {
            return false;
            }
        }
    }

    // Click handler using useMapEvents.
    const ClickHandler = () => {
        useMapEvents({
            click: (e) => {
                console.log("ClickHandler");

                // Otherwise, process marker placement only if a marker is selected.
                if (
                    e.originalEvent.target.closest("#sidebar-container") ||
                    e.originalEvent.target.closest("#settings-sidebar-container") ||
                    e.originalEvent.target.closest("#back-button-container") ||
                    e.originalEvent.target.closest("#score-selector")
                ) {
                    return;
                }
                const { lat, lng } = e.latlng;

                if (selectedMarker) {

                    if (isGarrisonLimitReached()) {
                        toast.info("Can't place more than 8 garrisons")
                        return;
                    }

                    if (boundChecker.isInsideBounds(lat, lng, layout)) {
                        if (selectedMarker.type === MarkerTypes.GARRISON) {
                            if (selectedMarker.faction === MarkerFaction.FRIENDLY) {
                                if (boundChecker.isEnemyTerritory(lat, lng, layout, sessionObj.gameObj.selectedFaction, factions, ControlledDistance(sessionObj.gameObj.selectedFaction), SECTOR_SIZE, true)) {
                                    toast.info("Cannot place in enemy territory");
                                    return;
                                }
                            } else if (selectedMarker.faction === MarkerFaction.ENEMY) {
                                // reverse enemy territory by passing enemy faction as arguments
                                if (boundChecker.isEnemyTerritory(lat, lng, layout, getEnemyFaction(), factions, ControlledDistance(getEnemyFaction()), SECTOR_SIZE, true)) {
                                    toast.info("Cannot place in enemy territory");
                                    return;
                                }
                            }
                            if (boundChecker.isNeutralTerritory(lat, lng, layout, alliesScore, axisScore)) {
                                toast.info("Cannot place in neutral territory");
                                return;
                            } else if (boundChecker.isTooClose(lat, lng, selectedMarker, sessionObj.gameObj.markers)) {
                                toast.info("Too close to another garrison");
                                return;
                            }
                        }

                        localUpdateRef.current = true;
                        const newMarker = {
                            id: crypto.randomUUID(),
                            type: selectedMarker.type,
                            faction: selectedMarker.faction,
                            position: { lat, lng },
                        };
                        console.log("new marker: ", newMarker);

                        const updatedMarkers = [...sessionObj.gameObj.markers, newMarker];
                        const updatedSessionObj = {
                            ...sessionObj,
                            gameObj: {
                                ...sessionObj.gameObj,
                                markers: updatedMarkers,
                            },
                        };
                        setSessionObj(updatedSessionObj);
                    } else {
                        toast.info("Cannot place in restricted area");
                    }
                } else {
                    console.warn("No marker selected")
                }
            },
        });
        return null;
    };

    useEffect(() => {
        setSessionObj((prev) => {
            const updatedMarkers = prev.gameObj.markers.filter((marker) => {
                if (marker.type === MarkerTypes.GARRISON) {
                    if (marker.faction === MarkerFaction.FRIENDLY) {
                        return boundChecker.isFriendlyTerritory(marker.position.lat, marker.position.lng, layout, prev.gameObj.selectedFaction, factions, ControlledDistance(prev.gameObj.selectedFaction), SECTOR_SIZE, false);
                    } else {
                        return boundChecker.isEnemyTerritory(marker.position.lat, marker.position.lng, layout, prev.gameObj.selectedFaction, factions, ControlledDistance(prev.gameObj.selectedFaction), SECTOR_SIZE, false)
                    }
                }
                return true; // Keep other markers
            });

            if (updatedMarkers.length === prev.gameObj.markers.length) {
                return prev; // No changes, return the same state to prevent re-renders
            }

            localUpdateRef.current = true;
            return { ...prev, gameObj: {
                ...prev.gameObj,
                markers: updatedMarkers}
            };
        });
    }, [axisScore, alliesScore]);

    const handleLeftClick = (id) => {
        localUpdateRef.current = true;
        setSessionObj((prev) => {
            const updatedMarkers = prev.gameObj.markers.filter((marker) => marker.id !== id)
            const updatedSessionObj = { ...prev,
                gameObj: {
                    ...prev.gameObj,
                    markers: updatedMarkers
                }
            };
            console.log("removal: ", updatedSessionObj);
            return updatedSessionObj;
        });
    }

    function onSelectMarker(markerType, markerFaction) {
        setSelectedMarker({type: markerType, faction: markerFaction});
    }

    function onResetMarkers(markerFaction, showModal = true) {
        localUpdateRef.current = true;
        const resetMarkers = () => {
            setSessionObj((prev) => {
                return {
                    ...prev,
                    gameObj: {
                        ...prev.gameObj,
                        markers: prev.gameObj.markers.filter(marker => marker.faction !== markerFaction)
                    }
                };
            });
            toast.success("Markers reset!");
        };

        if (showModal) {
            setModalAction(() => resetMarkers);
            setIsResetModalOpen(true);
        } else {
            resetMarkers();
        }
    }


    function toggleShowRadius() {
        setShowRadius((prev) => !prev);
    }

    function toggleShowScore() {
        setShowScore((prev) => !prev);
    }

    function toggleShowGrid() {
        setShowGrid((prev) => !prev);
    }

    function toggleShowCaps() {
        setShowCaps((prev) => !prev);
    }

    const onShapeCreated = (e) => {
        const { layerType, layer } = e;
        let shapeData = {};

        if (layerType === "rectangle") {
            const bounds = layer.getBounds();
            shapeData = {
                bounds: [
                    [bounds.getSouthWest().lat, bounds.getSouthWest().lng],
                    [bounds.getNorthEast().lat, bounds.getNorthEast().lng],
                ],
            };
        } else if (layerType === "polygon" || layerType === "polyline") {
            const latlngs = layer.getLatLngs();
            shapeData = { latlngs };
        } else {
            shapeData = { geoJson: layer.toGeoJSON() };
        }

        // Remove the drawn layer from the map
        layer.remove();

        // Update state
        localUpdateRef.current = true;
        setSessionObj((prevSession) => ({
            ...prevSession,
            gameObj: {
                ...prevSession.gameObj,
                shapes: {
                    ...prevSession.gameObj.shapes,
                    [layerType === "rectangle" ? "rectangles" : layerType + "s"]: [
                        ...prevSession.gameObj.shapes[layerType === "rectangle" ? "rectangles" : layerType + "s"],
                        shapeData,
                    ],
                },
            },
        }));
    };



    // Similarly, you can handle editing and deletion:
    const onShapeEdited = (e) => {
        console.log("Shape edited:", e);
    };

    const onShapeDeleted = (e) => {
        console.log("Shape deleted:", e);
    };

    function handleChangeLayout() {
        console.log("Change Layout clicked");
        setShowSelectLayoutDialog(true)
    }


    function handleSwitchSide() {
        console.log("Switch Side clicked");
        setShowSelectFactionDialog(true)
    }

    function onSelectLayout(selected) {
        console.log("onSelectLayout")
        localUpdateRef.current = true;
        setSessionObj({
            ...sessionObj,
            gameObj: {
                ...sessionObj.gameObj,
                selectedCaps: selected,
            },
        });
        setShowSelectLayoutDialog(false)
    }

    function onSelectFaction(faction) {
        localUpdateRef.current = true;
        setSessionObj((prev) => {
            // Flip the faction for each marker if it has one.
            const updatedMarkers = prev.gameObj.markers.map(marker => {
                if (marker.faction === MarkerFaction.FRIENDLY) {
                    return { ...marker, faction: MarkerFaction.ENEMY };
                } else if (marker.faction === MarkerFaction.ENEMY) {
                    return { ...marker, faction: MarkerFaction.FRIENDLY };
                }
                return marker;
            });

            return {
                ...prev,
                gameObj: {
                    ...prev.gameObj,
                    selectedFaction: faction,
                    score: reverseScore(prev.gameObj.score),
                    markers: updatedMarkers,
                }
            };
        });
        setShowSelectFactionDialog(false);
        if (sessionObj.gameObj.selectedCaps?.length < 5) {
            setShowSelectLayoutDialog(true);
        }
    }

    function reverseScore(score) {
        const [first, second] = score.split(":"); // Split into two numbers
        return `${second}:${first}`; // Swap and return
    }

    function getTotalUsersInSession() {
        let total = 1;
        sessionObj.guests.forEach((user) => total++)
        return total;
    }

    useEffect(() => {
        // Only sync if the change was local.
        if (localUpdateRef.current) {
            console.log("Sync changes...")
            syncSessionObj(sessionObj);
            localUpdateRef.current = false; // reset the flag after syncing
        }
    }, [sessionObj]);

    return (
        <div>
            <MapContainer
                center={[imageHeight / 2, imageWidth / 2]}
                zoom={-1}
                minZoom={-3}
                maxZoom={4}
                wheelPxPerZoomLevel={400}
                zoomControl={false}
                doubleClickZoom={false}
                maxBounds={imageBounds}
                maxBoundsViscosity={1.0}
                style={{ width: "100%", height: "calc(100dvh - 50px)" }}
                crs={L.CRS.Simple}
            >
                <ImageOverlay url={`/img/leaflet-maps/${gameMap}.jpg`} bounds={imageBounds}/>

                {showScore && (
                    <div>
                        {layout === MapLayouts.HORIZONTAL && sessionObj.gameObj.selectedFaction != null && (
                            <div>
                                {/* Left Zone */}
                                <Polygon
                                    positions={[
                                        [400, 0], // Top-left
                                        [400, ControlledDistance(factions.left)], // Top-right (width varies by score)
                                        [1600, ControlledDistance(factions.left)], // Bottom-right
                                        [1600, 0], // Bottom-left
                                    ]}
                                    pathOptions={{
                                        fillColor: sessionObj.gameObj.selectedFaction === factions.left ? "#1e3a8a" : "#b91c1c",
                                        fillOpacity: 0.2,
                                        weight: 0,
                                    }}
                                />

                                {/* Right Zone */}
                                <Polygon
                                    positions={[
                                        [400, 2000 - ControlledDistance(factions.right)], // Top-left
                                        [400, 2000], // Top-right (width varies by score)
                                        [1600, 2000], // Bottom-right
                                        [1600, 2000 - ControlledDistance(factions.right)], // Bottom-left
                                    ]}
                                    pathOptions={{
                                        fillColor: sessionObj.gameObj.selectedFaction === factions.right ? "#1e3a8a" : "#b91c1c",
                                        fillOpacity: 0.2,
                                        weight: 0,
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}

                {showScore && (
                    <div>
                        { layout === MapLayouts.VERTICAL && sessionObj.gameObj.selectedFaction != null && (
                            <div>
                                {/* Lower Zone */}
                                <Polygon
                                    positions={[
                                        [ControlledDistance(factions.right), 400], // Top-left
                                        [ControlledDistance(factions.right), 1600], // Top-right (width varies by score)
                                        [0, 1600], // Bottom-right
                                        [0, 400], // Bottom-left
                                    ]}
                                    pathOptions={{
                                        color: "blue",
                                        fillColor: sessionObj.gameObj.selectedFaction === factions.right ? "#1e3a8a" : "#b91c1c",
                                        fillOpacity: 0.2,
                                        weight: 0 }}
                                />
                                {/* Upper Zone */}
                                <Polygon
                                    positions={[
                                        [2000, 400], // Top-left
                                        [2000, 1600], // Top-right (rest of the map)
                                        [2000 - ControlledDistance(factions.left), 1600], // Bottom-right
                                        [2000 - ControlledDistance(factions.left), 400], // Bottom-left
                                    ]}
                                    pathOptions={{
                                        color: "red",
                                        fillColor: sessionObj.gameObj.selectedFaction === factions.left ? "#1e3a8a" : "#b91c1c",
                                        fillOpacity: 0.2,
                                        weight: 0 }}
                                />
                            </div>
                        )}
                    </div>
                )}

                {showCaps && sessionObj.gameObj.selectedCaps.length > 0 && (
                    <div>
                        {getSelectedCapPoints().map((cap, sectionIndex) => (
                            <React.Fragment key={`section-${sectionIndex}-cap-${cap?.name || sectionIndex}`}>
                                <Circle
                                    center={[cap.position.lat, cap.position.lng]}
                                    radius={cap.radius}
                                    pathOptions={{ color: "#000000", fillOpacity: 0.1 }}
                                />
                                <Marker position={[cap.position.lat, cap.position.lng]} icon={L.divIcon({ html: "", iconSize: [0, 0] })}>
                                    <Tooltip
                                        permanent
                                        direction="top"
                                        offset={[0, -cap.radius]}
                                        className="no-box-tooltip"
                                    >
                                        {cap.name}
                                    </Tooltip>
                                </Marker>
                            </React.Fragment>
                        ))}
                    </div>
                )}

                <ClickHandler />

                <FeatureGroup>
                    {sessionObj.gameObj.shapes?.rectangles.map((rectangle, index) => (
                        <Rectangle key={`rectangle-${index}`} bounds={rectangle.bounds}
                        />
                    ))}

                    {sessionObj.gameObj.shapes?.polylines.map((polyline, index) => (
                        <Polyline key={`polyline-${index}`} positions={polyline.latlngs} />
                    ))}

                    {sessionObj.gameObj.shapes?.polygons.map((polygon, index) => (
                        <Polygon key={`polygon-${index}`} positions={polygon.latlngs} />
                    ))}

                    {selectedMarker == null && (
                        <EditControl
                            position="topright"
                            onCreated={onShapeCreated}
                            onEdited={onShapeEdited}
                            onDeleted={onShapeDeleted}
                            draw={{
                                polyline: true,
                                polygon: true,
                                rectangle: true,
                                circle: false,
                                marker: false,
                                circlemarker: false,
                            }}
                        />
                    )}
                </FeatureGroup>



                {sessionObj.gameObj.markers &&
                    sessionObj.gameObj.markers?.map((marker) => (
                        <React.Fragment key={marker.id}>
                            <Marker
                                position={marker.position}
                                icon={markerProperties[marker.type]?.[marker.faction].icon}
                                eventHandlers={{
                                    click: () => handleLeftClick(marker.id),
                                }}
                            />
                            {showRadius && markerProperties[marker.type]?.[marker.faction].radius && (
                                <Circle
                                    center={marker.position}
                                    radius={markerProperties[marker.type]?.[marker.faction].radius}
                                    pathOptions={{
                                        color: markerProperties[marker.type]?.[marker.faction].color,
                                        fillOpacity: 0,
                                        weight: 3,
                                        dashArray: "10, 10",
                                    }}
                                />
                            )}
                        </React.Fragment>
                    ))}

                    <div id="score-selector">
                        <select
                            value={sessionObj.gameObj.score}
                            onChange={(e) => {
                                localUpdateRef.current = true;
                                setSessionObj(prev => ({
                                    ...prev,
                                    gameObj: {
                                        ...prev.gameObj,
                                        score: e.target.value,
                                    },
                                }));
                            }
                        }
                        >
                            <option value="1:4">1:4</option>
                            <option value="2:3">2:3</option>
                            <option value="2:2">2:2</option>
                            <option value="3:2">3:2</option>
                            <option value="4:1">4:1</option>
                        </select>
                    </div>

                {showSelectLayoutDialog && !showSelectFactionDialog && (
                    <SelectLayoutDialog
                        onCancel={() => setShowSelectLayoutDialog(false)}  // Close on cancel
                        onConfirm={onSelectLayout}  // Pass function to handle selection
                        selectedCaps={sessionObj.gameObj.selectedCaps}
                        layout={layout}
                    />
                )}

                {showSelectFactionDialog && (
                    <SelectFactionDialog
                        selectedFaction={sessionObj.gameObj.selectedFaction}
                        onCancel={() => setShowSelectFactionDialog(false)}
                        onConfirm={onSelectFaction}
                    />
                )}

                <div id="back-button-container">
                    <button id="back-button" onClick={() => navigate("/")}>
                        Back
                    </button>
                </div>

                <div id="session-management">
                    { sessionObj.sessionId != null && (
                        <>
                            <ul id="user-list">
                                <li className="user-item">
                                    Users: {getTotalUsersInSession()}
                                </li>
                            </ul>
                        </>
                    )}
                </div>

                <SettingsSidebar
                    sessionId={sessionObj.sessionId}
                    createSession={createSession}
                    leaveSession={leaveSession}
                    handleChangeLayout={handleChangeLayout}
                    handleSwitchSide={handleSwitchSide}
                    toggleShowGrid={toggleShowGrid}
                    showGrid={showGrid}
                    showCaps={showCaps}
                    toggleShowCaps={toggleShowCaps}
                    showRadius={showRadius}
                    toggleShowRadius={toggleShowRadius}
                    showScore={showScore}
                    toggleShowScore={toggleShowScore}
                />

                <Sidebar
                    selectedMarker={selectedMarker}
                    onRemoveSelectedMarker={() => setSelectedMarker(null)}
                    onResetMarkers={onResetMarkers}
                    onSelectMarker={onSelectMarker}
                />

                {isInviteModalOpen && sessionObj.sessionId != null && (
                    <InviteDialog title="Invite Others" link={sessionUrl} onClose={() => setIsInviteModalOpen(null)} />
                )}

                {isResetModalOpen && (
                    <ConfirmDialog
                        title="Reset Markers"
                        description="Are you sure you want to reset all markers?"
                        onCancel={() => setIsResetModalOpen(null)}
                        onSuccess={() => {
                            if (modalAction) modalAction();
                            setIsResetModalOpen(false);
                        }}
                    />
                )}
                { showGrid &&
                    <GridOverlay />
                }

                {loading &&
                    <LoadingDialog title={loadingTitle}
                                   loading={loading}
                                   onCancel={leaveSession}
                    ></LoadingDialog>
                }

            </MapContainer>
        </div>
    );
}